import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { newsFormSchema, type NewsFormData } from '@/schemas/newsSchemas';
import type { News } from '@/types/legacy';
import { Loader2 } from 'lucide-react';

interface NewsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  news?: News | null;
  onSuccess?: () => void;
}

export function NewsFormDialog({ open, onOpenChange, news, onSuccess }: NewsFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const form = useForm<NewsFormData>({
    resolver: zodResolver(newsFormSchema),
    values: {
      title: news?.title || '',
      headline: news?.headline || '',
      content: news?.content || '',
      development: news?.development || '',
      url: news?.url || '',
      priority: news?.priority || 0,
      is_active: news?.is_active ?? true,
    },
  });

  const onSubmit = async (data: NewsFormData) => {
    setLoading(true);
    try {
      const payload = {
        title: data.title,
        headline: data.headline || null,
        content: data.content || null,
        development: data.development || null,
        url: data.url || null,
        priority: data.priority,
        is_active: data.is_active,
      };

      if (news) {
        const { error } = await api
          .from('news')
          .update(payload)
          .eq('id', news.id);

        if (error) throw error;

        toast({
          title: 'Noticia actualizada',
          description: 'La noticia se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('news')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Noticia creada',
          description: 'La noticia se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['news'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la noticia',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{news ? 'Editar Noticia' : 'Nueva Noticia'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Título de la noticia" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="headline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Titular</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Titular destacado (opcional)" />
                  </FormControl>
                  <FormDescription>
                    Titular breve que se muestra de forma destacada
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contenido</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Contenido principal" rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="development"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Desarrollo</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Desarrollo detallado de la noticia" rows={6} />
                  </FormControl>
                  <FormDescription>
                    Información adicional y detalles extendidos
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL</FormLabel>
                  <FormControl>
                    <Input {...field} type="url" placeholder="https://ejemplo.com" />
                  </FormControl>
                  <FormDescription>
                    Enlace externo relacionado con la noticia
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridad (0-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 0)}
                      min={0}
                      max={10}
                      placeholder="0"
                    />
                  </FormControl>
                  <FormDescription>
                    0 = Baja, 3-4 = Media, 5+ = Alta
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activa</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      La noticia es visible para todos los usuarios
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {news ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
